import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable, ReportColumn } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { formatCents, formatDate } from '@/lib/utils';
import DateRangePresets, {
  type Preset,
  boundsFor,
  rangeReady,
} from '@/components/DateRangePresets';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ItemSalesRow {
  date: string;
  number: string;
  party: string;
  item: string;
  qty: number;
  unit_price: number;
  amount: number;
  ref_id: number;
}

interface ItemSalesData {
  from: string;
  to: string;
  rows: ItemSalesRow[];
  total_amount: number;
  total_qty: number;
  count: number;
}

interface ItemSalesResponse {
  data: ItemSalesData;
}

// ─── Sort options ────────────────────────────────────────────────────────────────

const SORTS: { value: 'asc' | 'desc'; label: string }[] = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
];

/** Qty formatter — thousands separators, up to 2 decimals (matches sq-ft fractions). */
function formatQty(value: number): string {
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 2 }).format(value);
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS: ReportColumn[] = [
  {
    key: 'date',
    header: 'Date',
    render: (value) => (value ? formatDate(String(value)) : '—'),
  },
  {
    key: 'number',
    header: 'Estimate No.',
    render: (value, row) => {
      const r = row as unknown as ItemSalesRow;
      const text = value == null ? '—' : String(value);
      if (r.ref_id != null) {
        return (
          <Link
            to={`/estimates/${r.ref_id}`}
            className="text-primary hover:underline underline-offset-4"
          >
            {text}
          </Link>
        );
      }
      return text;
    },
  },
  { key: 'party', header: 'Customer' },
  { key: 'item', header: 'Item' },
  {
    key: 'qty',
    header: 'Qty',
    align: 'right',
    render: (value) => (value == null ? '—' : formatQty(Number(value))),
  },
  { key: 'unit_price', header: 'Unit Price', money: true },
  { key: 'amount', header: 'Amount', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ItemSalesReport() {
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');

  const { from, to } = useMemo(
    () => boundsFor(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'item-sales', from, to, sort],
    queryFn: () =>
      apiFetch<ItemSalesResponse>(`/api/reports/item-sales?from=${from}&to=${to}&sort=${sort}`),
    enabled: rangeReady(preset, customFrom, customTo),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Item Sales</h1>
        <ExportButtons endpoint="/api/reports/item-sales" params={{ from, to, sort }} />
      </div>

      {/* Date-range presets */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DateRangePresets
          preset={preset}
          onPresetChange={setPreset}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
          idPrefix="is"
        />

        {/* Sort order */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 ml-auto">
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSort(s.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                sort === s.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <>
          {/* Summary line */}
          <p className="text-sm text-muted-foreground mb-3">
            <span className="font-medium text-foreground">{report.count}</span> lines ·{' '}
            <span className="font-medium text-foreground">Qty {formatQty(report.total_qty)}</span> ·{' '}
            <span className="font-medium text-foreground">
              Total {formatCents(report.total_amount)}
            </span>
          </p>

          {/* Line items */}
          <ReportTable columns={COLUMNS} rows={rows} />
        </>
      )}
    </div>
  );
}
